import type { Namespace } from '../types';

// DE drafted — queued for native review in the task file's "DE review queue".
// "Parent"/"Child" stay invariant (technical role names, match topics/API).
export const microcontrollers = {
  title: { de: 'Mikrocontroller', en: 'Microcontrollers' },
  desc: {
    de: 'ESP32-Controller des Standorts — Zeile öffnen für Details',
    en: 'ESP32 controllers reporting through the site — open a row for details',
  },
  registerDevice: { de: 'Gerät registrieren', en: 'Register Device' },

  totalDevices: { de: 'Geräte gesamt', en: 'Total devices' },
  online: { de: 'Online', en: 'Online' },
  offline: { de: 'Offline', en: 'Offline' },
  unknown: { de: 'Unbekannt', en: 'Unknown' },
  retired: { de: 'Ausgemustert', en: 'Retired' },
  unassigned: { de: 'Nicht zugewiesen', en: 'Unassigned' },

  colDevice: { de: 'Gerät', en: 'Device' },
  colAssignment: { de: 'Zuweisung', en: 'Assignment' },
  colStatus: { de: 'Status', en: 'Status' },
  colLastSeen: { de: 'Zuletzt gesehen', en: 'Last seen' },

  parentController: { de: 'Parent-Controller', en: 'Parent controller' },
  childController: { de: 'Child-Controller', en: 'Child controller' },
  parentType: { de: 'Parent · Raum-Ebene', en: 'Parent · room-level' },
  childType: { de: 'Child · Rack-Ebene', en: 'Child · rack-level' },

  retiredNote: {
    de: 'Dieses Gerät ist ausgemustert — schreibgeschützt. Der Verlauf bleibt erhalten.',
    en: 'This device is retired — read-only. Its history is kept.',
  },
  editDevice: { de: 'Gerät bearbeiten', en: 'Edit device' },
  retireDevice: { de: 'Gerät ausmustern', en: 'Retire device' },
  recordTitle: { de: 'Mikrocontroller – {name}', en: 'Microcontroller – {name}' },
  recordEditTitle: { de: 'Mikrocontroller {name} bearbeiten', en: 'Edit {name} Microcontroller' },
  macAddress: { de: 'MAC-Adresse', en: 'MAC address' },
  asgNotePrefix: { de: 'Zuweisung wird bearbeitet über', en: 'Assignment is edited from' },
  deviceId: { de: 'Geräte-ID', en: 'Device ID' },
  firmware: { de: 'Firmware', en: 'Firmware' },
  childNodes: { de: 'Child-Knoten', en: 'Child nodes' },
  registered: { de: 'Registriert', en: 'Registered' },

  retireTitle: { de: '{name} ausmustern?', en: 'Retire {name}?' },
  retireMsgPrefix: { de: 'Dies markiert', en: 'This marks' },
  retireMsgSuffix: {
    de: 'als Lebensende und hebt die Slot-Zuweisung auf. Die Ausmusterung ist endgültig — das Gerät kann nie reaktiviert werden, der Verlauf bleibt erhalten.',
    en: 'end-of-life and unassigns it from its slot. Retirement is permanent — the device can never be reactivated, but its history is kept.',
  },
  toastRetired: { de: 'Gerät ausgemustert', en: 'Device retired' },
  toastRetireFailed: { de: 'Ausmustern fehlgeschlagen: {error}', en: 'Retire failed: {error}' },

  saveMsg: { de: 'Anzeigenamen dieses Geräts aktualisieren.', en: "Update this device's display name." },
  toastUpdated: { de: 'Gerät aktualisiert', en: 'Device updated' },
  toastUpdateFailed: { de: 'Aktualisierung fehlgeschlagen: {error}', en: 'Update failed: {error}' },

  loadError: { de: 'Geräte konnten nicht geladen werden', en: "Couldn't load devices" },
  svcUnreachable: {
    de: 'Der Mikrocontroller-Dienst ist nicht erreichbar. Bitte gleich erneut versuchen.',
    en: 'The microcontroller service is unreachable. Retry shortly.',
  },
  emptyTitle: { de: 'Noch keine Mikrocontroller', en: 'No microcontrollers yet' },
  emptyMsg: {
    de: 'Registrieren Sie ein Gerät, um es hier zu überwachen.',
    en: 'Register a device to start monitoring it here.',
  },

  register: { de: 'Registrieren', en: 'Register' },
  registerConfirmTitle: { de: 'Gerät registrieren?', en: 'Register device?' },
  registerConfirmMsg: {
    de: 'Der Controller wird registriert und beginnt zu melden, sobald er sich verbindet.',
    en: 'The controller will be registered and begin reporting once it connects.',
  },
  toastRegistered: { de: 'Gerät registriert', en: 'Device registered' },
  toastRegisterFailed: { de: 'Registrierung fehlgeschlagen: {error}', en: 'Register failed: {error}' },
  egNodeName: { de: 'z. B. Node-A1', en: 'e.g. Node-A1' },
  macHint: {
    de: 'Die Hardware-MAC vom Controller-Etikett.',
    en: 'The hardware MAC from the controller label.',
  },
  macInvalid: {
    de: 'Geben Sie eine gültige MAC-Adresse ein — sechs durch Doppelpunkte getrennte Hex-Paare (aa:bb:cc:dd:ee:ff).',
    en: 'Enter a valid MAC address — six colon-separated hex pairs (aa:bb:cc:dd:ee:ff).',
  },

  // Auto-discovered channels (R-097 P3): the inventory the controller announced.
  searchPlaceholder: { de: 'Suche nach Name oder MAC…', en: 'Search by name or MAC…' },
  noMatch: { de: 'Keine passenden Controller', en: 'No matching controllers' },
  colChannel: { de: 'Kanal', en: 'Channel' },
  colModel: { de: 'Modell', en: 'Model' },
  colHealth: { de: 'Zustand', en: 'Health' },
  discoveredChannels: { de: 'Erkannte Kanäle', en: 'Discovered channels' },
  sensor: { de: 'Sensor', en: 'Sensor' },
  actuator: { de: 'Aktor', en: 'Actuator' },
  healthy: { de: 'OK', en: 'Healthy' },
  faulty: { de: 'Störung', en: 'Faulty' },
} as const satisfies Namespace;
